import type { EditorToolKind } from "@/stores/review-editor";

/**
 * Tool grid — configurable button row for both canvas surfaces.
 *
 * The caller passes the tool set it supports; this component renders
 * a labeled button per tool with an inline-SVG icon. Each button has
 * `data-canvas-tool="<kind>"` for Playwright targeting and an
 * `aria-pressed` state for the active tool.
 *
 * Icons are inline path strings — same family as the legacy painter's
 * tool row so the visual chrome stays familiar.
 */

export type ToolGridProps = {
  tools: readonly EditorToolKind[];
  value: EditorToolKind;
  onChange: (tool: EditorToolKind) => void;
  /** Map of tool → keyboard-shortcut letter for the tooltip. Optional;
   * the legacy tactical-board mappings are pre-populated below. */
  shortcuts?: Partial<Record<EditorToolKind, string>>;
};

const DEFAULT_SHORTCUTS: Record<EditorToolKind, string> = {
  select: "V",
  arrow: "A",
  line: "L",
  zone: "Z",
  circle: "C",
  label: "T",
  freehand: "F",
  player: "P",
  ball: "B",
  spotlight: "S",
  dim: "D",
  formation: "G",
};

const TOOL_LABELS: Record<EditorToolKind, string> = {
  select: "Select",
  arrow: "Arrow",
  line: "Line",
  zone: "Zone",
  circle: "Circle",
  label: "Label",
  freehand: "Pen",
  player: "Player",
  ball: "Ball",
  spotlight: "Spotlight",
  dim: "Dim",
  formation: "Formation",
};

/** Inline SVG path for each tool. Stroke-only (24×24 viewbox). */
const TOOL_ICONS: Record<EditorToolKind, JSX.Element> = {
  select: (
    <path d="M5 4l14 8-6 1-2 6-6-15z" fill="currentColor" stroke="none" />
  ),
  arrow: <path d="M4 18 L18 6 M18 6 H12 M18 6 V12" />,
  line: <path d="M4 18 L18 6" />,
  zone: (
    <rect x="4" y="6" width="16" height="12" rx="1" strokeDasharray="3 2" />
  ),
  circle: <ellipse cx="12" cy="12" rx="9" ry="6" />,
  label: <path d="M5 6 H19 V12 H10 L7 18 V12 H5 Z" />,
  freehand: (
    <path d="M4 18 Q 6 8, 10 12 T 16 8 T 20 14" />
  ),
  player: <circle cx="12" cy="12" r="6" />,
  ball: <circle cx="12" cy="12" r="4" />,
  spotlight: (
    <g>
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="8" strokeDasharray="2 2" />
    </g>
  ),
  dim: <rect x="4" y="4" width="16" height="16" fillOpacity="0.4" />,
  formation: (
    <g>
      <circle cx="6" cy="6" r="2" />
      <circle cx="12" cy="10" r="2" />
      <circle cx="18" cy="6" r="2" />
      <circle cx="9" cy="18" r="2" />
      <circle cx="15" cy="18" r="2" />
    </g>
  ),
};

export function ToolGrid(props: ToolGridProps) {
  const shortcuts = { ...DEFAULT_SHORTCUTS, ...(props.shortcuts ?? {}) };
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-display text-ink-muted uppercase tracking-wider">
        Tools
      </span>
      <div
        className="grid grid-cols-3 gap-1.5"
        role="toolbar"
        aria-label="Drawing tools"
      >
        {props.tools.map((tool) => {
          const active = tool === props.value;
          const label = TOOL_LABELS[tool];
          // Every shape is directly draggable now, so Select is the
          // explicit "no-draw / move things safely" mode — say so in
          // the tooltip (the button text stays compact).
          const title = tool === "select" ? "Select / Move" : label;
          const shortcut = shortcuts[tool];
          return (
            <button
              key={tool}
              type="button"
              aria-pressed={active}
              aria-label={`${title}${shortcut ? ` (${shortcut})` : ""}`}
              title={`${title}${shortcut ? ` (${shortcut})` : ""}`}
              data-canvas-tool={tool}
              onClick={() => props.onChange(tool)}
              className={`flex flex-col items-center justify-center gap-1 px-2 py-2 rounded border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                active
                  ? "bg-accent border-accent text-accent-ink"
                  : "bg-canvas border-rule text-ink-muted hover:border-ink-muted hover:bg-field hover:text-ink"
              }`}
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {TOOL_ICONS[tool]}
              </svg>
              <span className="text-[10px] font-mono leading-none">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
